<template>
  <!--
  USAGE:

  <OptionalInput
    v-model="foobarVariableName"
    :nullable="true"
    v-slot="{ slotValue, slotValueChanged, slotIsNull }"
  >
    <FoobarInputElement :modelValue="slotValue" @update:modelValue="slotValueChanged"></FoobarInputElement>
  </OptionalInput>

  STYLING:
  The element of the slot is wrapped by a `div.slot` which (next to the `.slot` class) has get either `.is-null` or `.not-null` applied.
  -->
  <div
    class="optional-input"
    :class="{
      [`${containedValueIsNull ? 'is' : 'not'}-null`]: true,
      linebreak: linebreak,
    }"
  >
    <div class="optional-input__header" v-if="title || nullable">
      <ion-label
        v-if="title"
        :style="!containedValueIsNull || !nullable ? {} : { color: '#808080' }"
        :position="linebreak ? 'stacked' : undefined"
        >{{ title }}</ion-label
      >
      <ion-toggle
        v-model="checkboxValue"
        @ion-change="checkboxChanged()"
        v-if="nullable"
      />
    </div>
    <div
      class="optional-input__slot"
      @update:modelValue="containedValueChanged"
      @click="onSlotClick"
    >
      <slot
        :slotValue="containedValue"
        :slotValueChanged="containedValueChanged"
        :slotIsNull="containedValueIsNull"
        @update:modelValue="containedValueChanged"
      >
        <!--
          Default: Simple text field.

          If you fill/override this slot:
          Remember to use `v-slot="{ slotValue, slotValueChanged, slotIsNull }"` and those changed variable names too.
          So, `slotValue*` instead of `containedValue*` below.
        -->
        <ion-input
          :modelValue="containedValue"
          @update:modelValue="containedValueChanged"
          :disabled="containedValueIsNull"
        ></ion-input>
      </slot>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, defineComponent, computed } from "vue";
import { IonToggle, IonLabel, IonInput } from "@ionic/vue";
import { undefinedToNull } from "@/helper/undefinedToNull";

defineComponent({
  name: "OptionalInput",
});

const props = withDefaults(
  defineProps<{
    nullable: boolean;
    title: string | null;
    modelValue: any | null;
    linebreak: boolean;
  }>(),
  {
    nullable: true,
    title: null,
    modelValue: null,
    linebreak: false,
  },
);
const emit = defineEmits<{
  (e: "update:modelValue", value: any | null): void;
}>();

// const isNull = computed(() => value.value === null)

const containedValue = ref(
  props.modelValue !== null ? props.modelValue : undefined
);
const checkboxValue = ref(containedValue.value !== undefined);
const containedValueIsNull = computed(() => !checkboxValue.value);

const updateValue = (value: any | null) => {
  emit("update:modelValue", value);
};
const somethingChanged = () => {
  let value = undefinedToNull(containedValue.value);
  if (props.nullable && containedValueIsNull.value) {
    value = null;
  }
  updateValue(value);
};
const containedValueChanged = (newValueFromEvent: any) => {
  containedValue.value = newValueFromEvent;
  somethingChanged();
};
const checkboxChanged = () => {
  somethingChanged();
};
const onSlotClick = () => {
  if (containedValueIsNull.value && props.nullable) {
    checkboxValue.value = true;
    checkboxChanged();
  }
};
</script>

<style scoped lang="scss">
.optional-input {
  align-items: center;
  display: flex;
  gap: 0.75em;
  max-width: 100%;
  flex: 2;
  min-width: 0;
}

.optional-input__header {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 0.75em;
  min-width: 0;
}

.optional-input__slot {
  flex: 2;
  max-width: 100%;
  min-width: 0;
}

ion-toggle {
  flex: 0 0 auto;
}

.linebreak {
  align-items: stretch;
  flex-direction: column;
  gap: 0.25em;
}

.linebreak .optional-input__header {
  width: 100%;
}

.linebreak .optional-input__slot {
  width: 100%;
}

.optional-input__slot :deep(ion-input),
.optional-input__slot :deep(ion-textarea),
.optional-input__slot :deep(ion-datetime),
.optional-input__slot :deep(ion-segment) {
  max-width: 100%;
  width: 100%;
}

@media (max-width: 430px) {
  .optional-input {
    align-items: stretch;
    flex-direction: column;
    gap: 0.25em;
    width: 100%;
  }

  .optional-input__header {
    justify-content: space-between;
    width: 100%;
  }

  .optional-input__slot {
    flex: 1 1 auto;
    width: 100%;
  }
}
</style>
